import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Form, Row, Col, Space, message } from 'antd';
import {
  theme,
  ButtonAnt,
  InputComponent,
  Text,
  SelectComponent,
} from '@pfmam/pfmam';
import { Box } from '../../../styles/globalStyle';
import { CaretDownOutlined } from '@ant-design/icons';
import { CreateBeneficiaryAddress, UpdateBeneficiaryAddress, GetStatesWithDescription } from '../../../actions/actions';

const ButtonWrapper = styled(Space)`
  justify-content: flex-end;
  width: 100%;
  min-width: 100%;
  margin-top: 24px;
  .primary {
    width: 160px;
  }
`;

const FormWrapper = styled.div`
  .ant-form-item {
    margin-bottom: 12px;
  }
  
  .ant-form-item-control {
    display: flex;
    flex-direction: column;
  }
  
  .ant-form-item-explain {
    min-height: 22px;
    line-height: 1.5;
    margin-top: 4px;
  }
`;

const SmallPlaceholder = styled.div`
  input::placeholder {
    font-size: 12px;
  }
`;
const BeneficiaryAddressModal = ({
  onClose,
  onSubmit,
  beneficiaryData,
  wireRecord,
  ssnorTIN,
  isEdit = false,
  setIsTouched,
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [states, setStates] = useState([]);

  useEffect(() => {
    GetStatesWithDescription().then((response) => {
      const stateList = [];
      if (response.data != null && response.data.length > 0) {
        response.data.map((val) => {
          const option = {};
          option.label = val.Code;
          option.value = val.Code;
          stateList.push(option);
        });
        setStates(stateList);
      }
    }).catch((err) => {
      console.log(err);
    });
  }, []);

  useEffect(() => {
    if (isEdit && beneficiaryData) {

      const transformedData = {
        department: beneficiaryData.Department || beneficiaryData.department || '',
        buildingNumber: beneficiaryData.BuildingNumber || beneficiaryData.buildingNumber || '',
        streetName: beneficiaryData.StreetName || beneficiaryData.streetName || '',
        buildingName: beneficiaryData.BuildingName || beneficiaryData.buildingName || '',
        floorNumber: beneficiaryData.FloorNumber || beneficiaryData.floorNumber || '',
        roomNumber: beneficiaryData.RoomNumber || beneficiaryData.roomNumber || '',
        zipCode: beneficiaryData.ZipCode || beneficiaryData.zipCode || '',
        city: beneficiaryData.City || beneficiaryData.city || '',
        county: beneficiaryData.County || beneficiaryData.county || '',
        state: beneficiaryData.State || beneficiaryData.state || '',
        country: 'US',
      };
      form.setFieldsValue(transformedData);
    } else {
      form.setFieldsValue({ country: 'US' });
    }
  }, [isEdit, beneficiaryData, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      // Trim leading and trailing spaces from all string fields
      Object.keys(values).forEach((key) => {
        if (typeof values[key] === 'string') {
          values[key] = values[key].trim();
        }
      });
      setIsSubmitting(true);


      const submittedData = {
        ...values,
        wireInstructionID: wireRecord?.wireInstructionID,
        SSNorTIN: ssnorTIN,
        ABANumber: wireRecord?.routing,
        BankName: wireRecord?.bankName,
        BankAccountNumber: wireRecord?.accnumber,
        submittedUser: null,
        beneficiaryAddressID: beneficiaryData?.BeneficiaryAddressID || null,
      };

      if (isEdit && beneficiaryData?.BeneficiaryAddressID) {
        const response = await UpdateBeneficiaryAddress(submittedData);
        message.success('Beneficiary Address updated successfully');
      } else {
        // Create new beneficiary address
        const response = await CreateBeneficiaryAddress(submittedData);
        message.success('Beneficiary Address created successfully');
      }

      setIsSubmitting(false);
      if (setIsTouched) setIsTouched(false);
      onSubmit(values);
      form.resetFields();
    } catch (error) {
      setIsSubmitting(false);
      if (error.response && error.response.data) {
        // API error response
        message.error(error.response.data.message || 'Failed to process beneficiary address');
      } else if (error.message) {
        // Network or other errors
        message.error(error.message);
      } else {
        // Validation errors
        message.error('Failed to process beneficiary address. Please check the form.');
      }
      console.log('Error:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    if (setIsTouched) setIsTouched(false);
    onClose();
  };

  // Validation for zip code: max 10 chars, only numbers and hyphens
  const validateZipCode = (_, value) => {
    if (!value || !value.trim()) {
      return Promise.reject(new Error('Zip Code is required'));
    }
    if (value.length > 16) {
      return Promise.reject(new Error('Zip Code must not exceed 16 characters'));
    }
    const zipCodePattern = /^[0-9\-]*$/;
    if (!zipCodePattern.test(value)) {
      return Promise.reject(new Error('Zip Code can only contain numbers and hyphens'));
    }
    return Promise.resolve();
  };

  return (
    <>
      <FormWrapper>
        <Form
          form={form}
          name="beneficiaryAddress"
          layout="vertical"
          requiredMark={true}
          className="forms"
          onFieldsChange={() => { if (setIsTouched) setIsTouched(true); }}
        >
          <Row gutter={24}>
            <Col span={24}>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label="Building Number"
                    normalize={(value) => value?.replace(/[^\x20-\x7E]/g, '').replace(/^\s+/, '')}
                    name="buildingNumber"
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: 'Building Number is required',
                      }
                    ]}
                  >
                    <InputComponent
                      type="default"
                      placeholder="Enter Building Number"
                      maxLength={16}
                      tabIndex={1}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="City"
                    normalize={(value) => value?.replace(/[^\x20-\x7E]/g, '').replace(/^\s+/, '')}
                    name="city"
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: 'City is required',
                      },
                    ]}
                  >
                    <InputComponent
                      type="default"
                      placeholder="Enter City"
                      maxLength={35}
                      tabIndex={7}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label="Street Name"
                    name="streetName"
                    normalize={(value) => value?.replace(/[^\x20-\x7E]/g, '').replace(/^\s+/, '')}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: 'Street Name is required',
                      }
                    ]}
                  >
                    <InputComponent
                      type="default"
                      placeholder="Enter Street Name"
                      maxLength={70}
                      tabIndex={2}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="State"
                    name="state"
                    rules={[
                      {
                        required: true,
                        message: 'State is required',
                      }
                    ]}
                  >
                    <SelectComponent
                      option={states}
                      placeholder="Select State"
                      tabIndex={8}
                      suffixIcon={<CaretDownOutlined />}
                      dropdownClassName="form-select-dropdown"
                      bordered={true}
                      getPopupContainer={(trigger) => trigger.parentNode}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <SmallPlaceholder>
                    <Form.Item
                      label="Building Name"
                      name="buildingName"
                      normalize={(value) => value?.replace(/[^\x20-\x7E]/g, '').replace(/^\s+/, '')}
                      rules={[
                        {
                          required: false,
                        }
                      ]}
                    >
                      <InputComponent
                        type="default"
                        placeholder="Enter Building Name"
                        maxLength={35}
                        tabIndex={3}
                      />
                    </Form.Item>
                  </SmallPlaceholder>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Zip Code"
                    normalize={(value) => value?.replace(/[^\x20-\x7E]/g, '').replace(/^\s+/, '')}
                    name="zipCode"
                    rules={[
                      {
                        required: true,
                        validator: validateZipCode,
                      }
                    ]}
                  >
                    <InputComponent
                      type="default"
                      placeholder="Enter Zip Code (e.g., 12345 or 12345-6789)"
                      maxLength={16}
                      tabIndex={9}
                    />
                  </Form.Item>
                </Col>

              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label="Department"
                    name="department"
                    normalize={(value) => value?.replace(/[^\x20-\x7E]/g, '').replace(/^\s+/, '')}
                    rules={[
                      {
                        required: false
                      }
                    ]}
                  >
                    <InputComponent
                      type="default"
                      placeholder="Enter Department"
                      maxLength={70}
                      tabIndex={4}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="County"
                    normalize={(value) => value?.replace(/[^\x20-\x7E]/g, '').replace(/^\s+/, '')}
                    name="county"
                    rules={[
                      {
                        required: false
                      }
                    ]}
                  >
                    <InputComponent
                      type="default"
                      placeholder="Enter County"
                      maxLength={35}
                      tabIndex={10}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={6}>
                  <SmallPlaceholder>
                    <Form.Item
                      label="Floor Number"
                      name="floorNumber"
                      normalize={(value) => value?.replace(/[^\x20-\x7E]/g, '').replace(/^\s+/, '')}
                      rules={[
                        {
                          required: false,
                        }
                      ]}
                    >
                      <InputComponent
                        type="default"
                        placeholder="Enter Floor Number"
                        maxLength={70}
                        tabIndex={5}
                      />
                    </Form.Item>
                  </SmallPlaceholder>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Room Number"
                    normalize={(value) => value?.replace(/[^\x20-\x7E]/g, '').replace(/^\s+/, '')}
                    name="roomNumber"
                    rules={[
                      {
                        required: false,
                      }
                    ]}
                  >
                    <InputComponent
                      type="default"
                      placeholder="Enter Room Number"
                      maxLength={70}
                      tabIndex={6}
                    />
                  </Form.Item>
                </Col>
            
                <Col span={12}>
                  <Form.Item
                    label="Country"
                    name="country"
                    rules={[
                      {
                        required: true,
                        message: 'Country is required',
                      },
                    ]}
                  >
                    <InputComponent
                      type="default"
                      placeholder="US"
                      disabled={true}
                      tabIndex={11}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} />
              </Row>

            </Col>

          </Row>

          <ButtonWrapper size={12}>
            <ButtonAnt
              type="default"
              label="Cancel"
              onClick={handleCancel}
              disabled={isSubmitting}
            />
            <ButtonAnt
              type="primary"
              label="Save"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
            />
          </ButtonWrapper>
        </Form>
      </FormWrapper>
    </>
  );
};

export default BeneficiaryAddressModal;
